import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Settings, FileText, Trash2, BookOpen, Plus, Eye, Pencil, RefreshCw,
  FileSignature, Copy, CheckCircle2, Loader2, Share2, Bell, Mail,
  Cpu, KeyRound, ExternalLink, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from '../hooks/use-toast';
import { obtenerToken } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;
const tokenHeader = () => ({ headers: { Authorization: `Bearer ${obtenerToken()}` } });

export default function AnalisisHipotecasConfig() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('general');

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="config-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/expedientes/analisis-hipotecas')} data-testid="btn-back">
          <ArrowLeft className="w-4 h-4 mr-1" />Volver
        </Button>
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6 text-purple-600" />
            Configuración Análisis IA Hipotecas
          </h1>
          <p className="text-sm text-gray-600">Skills (instrucciones IA) y plantillas de documentos generables</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">
            <Settings className="w-4 h-4 mr-2" />General
          </TabsTrigger>
          <TabsTrigger value="skills" data-testid="tab-skills">
            <BookOpen className="w-4 h-4 mr-2" />Skills y Jurisprudencia
          </TabsTrigger>
          <TabsTrigger value="plantillas" data-testid="tab-plantillas">
            <FileSignature className="w-4 h-4 mr-2" />Plantillas de documentos
          </TabsTrigger>
          <TabsTrigger value="proveedores-ia" data-testid="tab-proveedores-ia">
            <Cpu className="w-4 h-4 mr-2" />Proveedores IA
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4 mt-4">
          <ConfigGeneralSection />
        </TabsContent>
        <TabsContent value="skills" className="space-y-4 mt-4">
          <SkillsSection />
        </TabsContent>
        <TabsContent value="plantillas" className="space-y-4 mt-4">
          <PlantillasSection />
        </TabsContent>
        <TabsContent value="proveedores-ia" className="space-y-4 mt-4">
          <ProveedoresIASection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// SECCIÓN: Skills
// ============================================================================
function SkillsSection() {
  const [builtins, setBuiltins] = useState([]);
  const [custom, setCustom] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null); // { nombre, contenido }
  const [savingEdit, setSavingEdit] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([
        axios.get(`${API}/api/hipotecas/skills/builtin`, tokenHeader()),
        axios.get(`${API}/api/hipotecas/skills`, tokenHeader()),
      ]);
      setBuiltins(b.data);
      setCustom(c.data);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar los skills', variant: 'destructive' });
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const verSkill = async (nombre) => {
    try {
      const r = await axios.get(`${API}/api/hipotecas/skills/builtin/${encodeURIComponent(nombre)}`, tokenHeader());
      setViewing({ nombre, contenido: r.data.contenido });
    } catch {
      toast({ title: 'Error', description: 'No se pudo abrir el skill', variant: 'destructive' });
    }
  };

  const subirSkill = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const nombre = window.prompt('Nombre del skill:', file.name.replace(/\.md$/, ''));
    if (!nombre) return;
    const fd = new FormData();
    fd.append('nombre', nombre);
    fd.append('tipo', 'skill');
    fd.append('descripcion', '');
    fd.append('archivo', file);
    try {
      await axios.post(`${API}/api/hipotecas/skills`, fd, { ...tokenHeader(), headers: { ...tokenHeader().headers, 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Skill añadido' });
      await loadAll();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      e.target.value = '';
    }
  };

  const borrarCustom = async (id) => {
    if (!window.confirm('¿Borrar este skill?')) return;
    try {
      await axios.delete(`${API}/api/hipotecas/skills/${id}`, tokenHeader());
      toast({ title: 'Borrado' });
      await loadAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const editarBuiltin = async (nombre) => {
    try {
      const r = await axios.get(`${API}/api/hipotecas/skills/builtin/${encodeURIComponent(nombre)}`, tokenHeader());
      setEditing({ nombre, contenido: r.data.contenido });
    } catch {
      toast({ title: 'Error', description: 'No se pudo abrir el skill para editar', variant: 'destructive' });
    }
  };

  const guardarBuiltin = async () => {
    if (!editing) return;
    if (!editing.contenido || editing.contenido.trim().length < 10) {
      toast({ title: 'Contenido demasiado corto', variant: 'destructive' });
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(
        `${API}/api/hipotecas/skills/builtin/${encodeURIComponent(editing.nombre)}`,
        { contenido: editing.contenido },
        tokenHeader(),
      );
      toast({ title: 'Skill actualizado', description: 'Se guardó una copia de la versión anterior en .backups/' });
      setEditing(null);
      await loadAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSavingEdit(false);
    }
  };

  const borrarBuiltin = async (nombre) => {
    const msg = nombre === 'SKILL.md'
      ? '⚠️ SKILL.md es el prompt PRINCIPAL del análisis. Si lo eliminas, los nuevos análisis FALLARÁN hasta que subas uno nuevo. ¿Eliminar de todas formas?'
      : `¿Eliminar el skill "${nombre}"? Se moverá a la papelera (.deleted/) y podrá recuperarse vía SSH si fuera necesario.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/api/hipotecas/skills/builtin/${encodeURIComponent(nombre)}`, tokenHeader());
      toast({ title: 'Skill eliminado' });
      await loadAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const reemplazarBuiltin = async (nombre, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.md')) {
      toast({ title: 'Formato inválido', description: 'Se requiere archivo .md', variant: 'destructive' });
      return;
    }
    if (!window.confirm(`¿Reemplazar "${nombre}" con el archivo "${file.name}"? Se hará backup automático de la versión actual.`)) return;
    const fd = new FormData();
    fd.append('archivo', file);
    try {
      const r = await axios.post(
        `${API}/api/hipotecas/skills/builtin/${encodeURIComponent(nombre)}/reemplazar`,
        fd,
        { ...tokenHeader(), headers: { ...tokenHeader().headers, 'Content-Type': 'multipart/form-data' } },
      );
      toast({ title: r.data.accion === 'restaurado' ? 'Skill restaurado' : 'Skill reemplazado', description: `${(r.data.tamano_bytes / 1024).toFixed(1)} KB` });
      await loadAll();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const tieneSkillPrincipal = builtins.some((b) => b.nombre === 'SKILL.md');

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-purple-500" />
            Skills incorporados
          </CardTitle>
          <CardDescription>
            Vienen con el sistema. SKILL.md es el prompt principal. Las jurisprudencias se usan como referencia documental.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!tieneSkillPrincipal && (
            <div className="mb-4 border border-amber-300 bg-amber-50 rounded p-3 flex items-center justify-between flex-wrap gap-2" data-testid="warn-no-skill">
              <div className="flex items-start gap-2">
                <BookOpen className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900 text-sm">Falta SKILL.md — el prompt principal</p>
                  <p className="text-xs text-amber-800">Los nuevos análisis fallarán hasta que subas un nuevo SKILL.md. Sube un archivo .md para restaurarlo.</p>
                </div>
              </div>
              <label>
                <Button asChild size="sm" data-testid="btn-restaurar-skill">
                  <span className="cursor-pointer">
                    <Plus className="w-4 h-4 mr-2" />Subir SKILL.md
                  </span>
                </Button>
                <input type="file" hidden accept=".md,text/markdown" onChange={(e) => reemplazarBuiltin('SKILL.md', e)} data-testid="input-restaurar-skill" />
              </label>
            </div>
          )}
          <div className="space-y-2">
            {builtins.map((b) => (
              <div key={b.nombre} className="flex items-center justify-between border rounded p-3 hover:bg-gray-50" data-testid={`builtin-${b.nombre}`}>
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-purple-400" />
                  <div>
                    <p className="font-medium text-sm">{b.nombre}</p>
                    <p className="text-xs text-gray-500">{(b.tamano_bytes / 1024).toFixed(1)} KB</p>
                  </div>
                  <Badge variant="outline">{b.tipo}</Badge>
                  {b.nombre === 'SKILL.md' && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300">prompt principal</Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => verSkill(b.nombre)} data-testid={`btn-ver-${b.nombre}`}>
                    <Eye className="w-4 h-4 mr-1" />Ver
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => editarBuiltin(b.nombre)} data-testid={`btn-editar-${b.nombre}`}>
                    <Pencil className="w-4 h-4 mr-1" />Editar
                  </Button>
                  <label>
                    <Button asChild size="sm" variant="ghost" data-testid={`btn-reemplazar-${b.nombre}`}>
                      <span className="cursor-pointer">
                        <RefreshCw className="w-4 h-4 mr-1" />Reemplazar
                      </span>
                    </Button>
                    <input type="file" hidden accept=".md,text/markdown" onChange={(e) => reemplazarBuiltin(b.nombre, e)} />
                  </label>
                  <Button size="sm" variant="ghost" onClick={() => borrarBuiltin(b.nombre)} data-testid={`btn-borrar-builtin-${b.nombre}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Skills personalizados del despacho</CardTitle>
              <CardDescription>Añade tus propias instrucciones o jurisprudencia. Se aplican junto al skill base.</CardDescription>
            </div>
            <label>
              <Button asChild data-testid="btn-add-skill">
                <span className="cursor-pointer">
                  <Plus className="w-4 h-4 mr-2" />Subir skill .md
                </span>
              </Button>
              <input type="file" hidden accept=".md,.txt" onChange={subirSkill} data-testid="input-skill" />
            </label>
          </div>
        </CardHeader>
        <CardContent>
          {custom.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">Aún no hay skills personalizados.</p>
          ) : (
            <div className="space-y-2">
              {custom.map((s) => (
                <div key={s.id} className="flex items-center justify-between border rounded p-3">
                  <div>
                    <p className="font-medium text-sm">{s.nombre}</p>
                    <p className="text-xs text-gray-500">{s.descripcion || s.tipo}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => borrarCustom(s.id)} data-testid={`btn-borrar-skill-${s.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{viewing?.nombre}</DialogTitle>
          </DialogHeader>
          <pre className="text-xs whitespace-pre-wrap font-mono bg-gray-50 p-4 rounded">{viewing?.contenido}</pre>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto" data-testid="dialog-editar-builtin">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-purple-600" />
              Editar {editing?.nombre}
            </DialogTitle>
            <CardDescription>
              Al guardar se hará una copia de la versión actual en <code>.backups/</code> dentro de la carpeta de skills.
              {editing?.nombre === 'SKILL.md' && (
                <span className="block mt-1 text-amber-700 font-medium">
                  ⚠️ Este es el prompt principal que rige TODO el análisis. Cambios mal formados pueden romper el sistema.
                </span>
              )}
            </CardDescription>
          </DialogHeader>
          <Textarea
            value={editing?.contenido || ''}
            onChange={(e) => setEditing((prev) => prev ? { ...prev, contenido: e.target.value } : prev)}
            className="font-mono text-xs min-h-[60vh]"
            placeholder="Contenido del skill en Markdown…"
            data-testid="textarea-editar-builtin"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={savingEdit}>
              Cancelar
            </Button>
            <Button onClick={guardarBuiltin} disabled={savingEdit} data-testid="btn-guardar-builtin">
              {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// SECCIÓN: Plantillas
// ============================================================================
function PlantillasSection() {
  const [plantillas, setPlantillas] = useState([]);
  const [variables, setVariables] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, nombre, tipo, descripcion, contenido_md}
  const [variablesOpen, setVariablesOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copiedTag, setCopiedTag] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, v] = await Promise.all([
        axios.get(`${API}/api/hipotecas/plantillas`, tokenHeader()),
        axios.get(`${API}/api/hipotecas/plantillas/variables`, tokenHeader()),
      ]);
      setPlantillas(p.data);
      setVariables(v.data.variables || []);
    } catch {
      toast({ title: 'Error', description: 'No se pudieron cargar las plantillas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const subirArchivo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const nombre = window.prompt('Nombre de la plantilla:', file.name.replace(/\.(md|txt|docx)$/i, ''));
    if (!nombre) return;
    const tipo = window.prompt('Tipo (reclamacion_extrajudicial, hoja_encargo, informe_juridico, custom):', 'custom');
    const fd = new FormData();
    fd.append('nombre', nombre);
    fd.append('tipo', tipo || 'custom');
    fd.append('descripcion', '');
    fd.append('archivo', file);
    try {
      await axios.post(`${API}/api/hipotecas/plantillas/upload`, fd, {
        ...tokenHeader(),
        headers: { ...tokenHeader().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'Plantilla añadida' });
      await loadAll();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      e.target.value = '';
    }
  };

  const editar = async (id) => {
    try {
      const r = await axios.get(`${API}/api/hipotecas/plantillas/${id}`, tokenHeader());
      setEditing(r.data);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const borrar = async (id) => {
    if (!window.confirm('¿Borrar esta plantilla?')) return;
    try {
      await axios.delete(`${API}/api/hipotecas/plantillas/${id}`, tokenHeader());
      toast({ title: 'Plantilla borrada' });
      await loadAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const guardar = async () => {
    try {
      if (editing.id) {
        await axios.put(`${API}/api/hipotecas/plantillas/${editing.id}`, editing, tokenHeader());
      } else {
        await axios.post(`${API}/api/hipotecas/plantillas`, editing, tokenHeader());
      }
      toast({ title: 'Plantilla guardada' });
      setEditing(null);
      await loadAll();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const copiarTag = (tag) => {
    navigator.clipboard.writeText(tag);
    setCopiedTag(tag);
    setTimeout(() => setCopiedTag(null), 1500);
  };

  const categorias = [...new Set(variables.map((v) => v.categoria))];

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-purple-500" />
                Plantillas de documentos
              </CardTitle>
              <CardDescription>
                Define plantillas (.md/.docx) con variables tipo <code className="text-purple-600">{'{{cliente.nombre}}'}</code> que se rellenan automáticamente.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setVariablesOpen(true)} data-testid="btn-ver-variables">
                <Eye className="w-4 h-4 mr-2" />Ver variables disponibles
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditing({ nombre: '', tipo: 'custom', descripcion: '', contenido_md: '' })} data-testid="btn-nueva-plantilla">
                <Plus className="w-4 h-4 mr-2" />Nueva
              </Button>
              <label>
                <Button asChild data-testid="btn-subir-plantilla">
                  <span className="cursor-pointer">
                    <Plus className="w-4 h-4 mr-2" />Subir archivo
                  </span>
                </Button>
                <input type="file" hidden accept=".md,.txt,.docx" onChange={subirArchivo} data-testid="input-plantilla-archivo" />
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>
          ) : plantillas.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">Aún no hay plantillas. Crea una nueva o sube un archivo.</p>
          ) : (
            <div className="space-y-2" data-testid="lista-plantillas">
              {plantillas.map((p) => (
                <div key={p.id} className="flex items-center justify-between border rounded p-3 hover:bg-gray-50" data-testid={`plantilla-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm">{p.nombre}</p>
                      <Badge variant="outline">{p.tipo}</Badge>
                      {!p.activo && <Badge variant="secondary">Inactiva</Badge>}
                    </div>
                    {p.descripcion && <p className="text-xs text-gray-500 mt-1">{p.descripcion}</p>}
                    <p className="text-xs text-gray-400 mt-1 truncate">{p.contenido_preview}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => editar(p.id)} data-testid={`btn-editar-${p.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => borrar(p.id)} data-testid={`btn-borrar-${p.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Variables */}
      <Dialog open={variablesOpen} onOpenChange={setVariablesOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="dialog-variables">
          <DialogHeader>
            <DialogTitle>Variables disponibles en plantillas</DialogTitle>
            <CardDescription>Haz clic en cualquier variable para copiarla. Pégalas en tu plantilla y se rellenarán con los datos del análisis al generar el documento.</CardDescription>
          </DialogHeader>
          <div className="overflow-auto space-y-4 flex-1">
            {categorias.map((cat) => (
              <div key={cat}>
                <p className="text-xs uppercase tracking-wide font-bold text-purple-700 mb-1">{cat}</p>
                <div className="space-y-1">
                  {variables.filter((v) => v.categoria === cat).map((v) => (
                    <button
                      key={v.tag}
                      onClick={() => copiarTag(v.tag)}
                      className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 rounded border hover:border-purple-300 hover:bg-purple-50 transition group"
                      data-testid={`var-${v.tag}`}
                    >
                      <code className="text-xs text-purple-700 font-mono whitespace-nowrap">{v.tag}</code>
                      <span className="text-xs text-gray-600 flex-1">{v.descripcion}</span>
                      {copiedTag === v.tag ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400 group-hover:text-purple-600 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Editor */}
      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="dialog-editor-plantilla">
            <DialogHeader>
              <DialogTitle>{editing.id ? 'Editar plantilla' : 'Nueva plantilla'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 flex-1 overflow-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nombre *</Label>
                  <Input
                    value={editing.nombre}
                    onChange={(e) => setEditing({ ...editing, nombre: e.target.value })}
                    data-testid="input-plantilla-nombre"
                  />
                </div>
                <div>
                  <Label>Tipo</Label>
                  <Input
                    value={editing.tipo}
                    onChange={(e) => setEditing({ ...editing, tipo: e.target.value })}
                    placeholder="custom, reclamacion_extrajudicial, hoja_encargo…"
                    data-testid="input-plantilla-tipo"
                  />
                </div>
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={editing.descripcion || ''}
                  onChange={(e) => setEditing({ ...editing, descripcion: e.target.value })}
                  data-testid="input-plantilla-descripcion"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Contenido (Markdown con variables {'{{...}}'})</Label>
                  <Button size="sm" variant="ghost" onClick={() => setVariablesOpen(true)} data-testid="btn-ver-vars-editor">
                    <Eye className="w-4 h-4 mr-1" />Ver variables
                  </Button>
                </div>
                <Textarea
                  value={editing.contenido_md}
                  onChange={(e) => setEditing({ ...editing, contenido_md: e.target.value })}
                  rows={18}
                  className="font-mono text-xs"
                  placeholder={'# Título de la plantilla\n\nEstimado/a {{cliente.nombre}},\n\nLe escribimos en relación a su préstamo hipotecario con {{hipoteca.banco}}…'}
                  data-testid="textarea-plantilla-contenido"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={guardar} disabled={!editing.nombre || !editing.contenido_md} data-testid="btn-guardar-plantilla">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}


// ============================================================================
// SECCIÓN: Configuración General (toggles)
// ============================================================================
function ConfigGeneralSection() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/hipotecas/configuracion`, tokenHeader());
      setCfg(r.data);
    } catch {
      toast({ title: 'Error', description: 'No se pudo cargar la configuración', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/hipotecas/configuracion`, patch, tokenHeader());
      setCfg(r.data);
      toast({ title: 'Configuración guardada' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>;
  if (!cfg) return null;

  return (
    <>
      <Card data-testid="config-compartir">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Share2 className="w-5 h-5 text-purple-500" />
            Compartir informes con clientes (enlaces públicos)
          </CardTitle>
          <CardDescription>
            Permite generar enlaces públicos para que el cliente acceda al informe y firme la hoja de encargo online.
            Si tu flujo es <strong>principalmente presencial</strong>, deja esta opción desactivada.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow
            label="Habilitar compartir mediante enlace público"
            help="Muestra el botón 'Compartir' en el detalle de cada análisis."
            checked={cfg.enlace_publico_habilitado}
            onCheckedChange={(v) => save({ enlace_publico_habilitado: v })}
            disabled={saving}
            testid="toggle-enlace-publico"
          />
          <ToggleRow
            label="Email al despacho cuando el cliente acepta"
            help="Notifica al despacho automáticamente con el PDF adjunto."
            checked={cfg.notificacion_email_aceptacion}
            onCheckedChange={(v) => save({ notificacion_email_aceptacion: v })}
            disabled={saving || !cfg.enlace_publico_habilitado}
            testid="toggle-email-aceptacion"
          />
          <ToggleRow
            label="Email de confirmación al cliente"
            help="Envía un email de cortesía al cliente con copia del informe."
            checked={cfg.confirmacion_email_cliente}
            onCheckedChange={(v) => save({ confirmacion_email_cliente: v })}
            disabled={saving || !cfg.enlace_publico_habilitado}
            testid="toggle-email-cliente"
          />
        </CardContent>
      </Card>

      <Card data-testid="config-recordatorios">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-500" />
            Recordatorios automáticos
          </CardTitle>
          <CardDescription>
            Envía recordatorios automáticos a los clientes que abrieron el enlace pero no aceptaron el encargo.
            Requiere que el cron del servidor esté activo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ToggleRow
            label="Activar recordatorios automáticos"
            help="Envía hasta 2 recordatorios espaciados según los días configurados abajo."
            checked={cfg.recordatorios_automaticos_habilitado}
            onCheckedChange={(v) => save({ recordatorios_automaticos_habilitado: v })}
            disabled={saving || !cfg.enlace_publico_habilitado}
            testid="toggle-recordatorios"
          />
          <div className="grid grid-cols-2 gap-3 pl-12">
            <div>
              <Label className="text-xs">Primer recordatorio (días tras visita)</Label>
              <Input
                type="number" min={1} max={30}
                value={cfg.recordatorio_dias_1}
                onChange={(e) => setCfg({ ...cfg, recordatorio_dias_1: parseInt(e.target.value, 10) || 3 })}
                onBlur={() => save({ recordatorio_dias_1: cfg.recordatorio_dias_1 })}
                disabled={!cfg.recordatorios_automaticos_habilitado}
                data-testid="input-rec-1"
              />
            </div>
            <div>
              <Label className="text-xs">Segundo recordatorio (días tras visita)</Label>
              <Input
                type="number" min={2} max={60}
                value={cfg.recordatorio_dias_2}
                onChange={(e) => setCfg({ ...cfg, recordatorio_dias_2: parseInt(e.target.value, 10) || 7 })}
                onBlur={() => save({ recordatorio_dias_2: cfg.recordatorio_dias_2 })}
                disabled={!cfg.recordatorios_automaticos_habilitado}
                data-testid="input-rec-2"
              />
            </div>
          </div>
          {!cfg.enlace_publico_habilitado && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Activa primero el compartir mediante enlace público.
            </p>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function ToggleRow({ label, help, checked, onCheckedChange, disabled, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="flex-1">
        <p className={`font-medium text-sm ${disabled ? 'text-gray-400' : 'text-gray-800'}`}>{label}</p>
        {help && <p className="text-xs text-gray-500 mt-0.5">{help}</p>}
      </div>
      <Switch
        checked={!!checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        data-testid={testid}
      />
    </div>
  );
}

// ============================================================================
// SECCIÓN: Proveedores IA Externos (v17)
// ============================================================================
function ProveedoresIASection() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // proveedor en edición
  const [testing, setTesting] = useState(null); // provider clave que se testea
  const [testResult, setTestResult] = useState(null); // { provider, ok, mensaje }

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/hipotecas/proveedores-ia`, tokenHeader());
      setProviders(data);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudieron cargar los proveedores IA', variant: 'destructive' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleTest = async (provider) => {
    setTesting(provider);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/api/hipotecas/proveedores-ia/${provider}/test`, {}, tokenHeader());
      setTestResult({ provider, ok: true, mensaje: `Respuesta: "${data.respuesta}" (${data.duracion_s}s)` });
    } catch (e) {
      setTestResult({ provider, ok: false, mensaje: e?.response?.data?.detail || e.message });
    } finally { setTesting(null); }
  };

  const handleDelete = async (provider) => {
    if (!window.confirm(`¿Borrar la configuración de ${provider}?`)) return;
    try {
      await axios.delete(`${API}/api/hipotecas/proveedores-ia/${provider}`, tokenHeader());
      toast({ title: 'Eliminado', description: provider });
      cargar();
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  return (
    <>
      <Card data-testid="proveedores-ia-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <KeyRound className="w-5 h-5 text-purple-600" />
            Proveedores IA externos (API keys propias)
          </CardTitle>
          <CardDescription>
            Configura tus propias API keys para usar IA sin pasar por el proxy de Emergent.
            Soporta proveedores OpenAI-compatible y APIs nativas (Claude, Gemini).
            Las claves se guardan en claro en la BD del servidor (solo accesibles vía SSH/root).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />Cargando…
            </div>
          ) : (
            <div className="space-y-2">
              {providers.map(p => (
                <div key={p.provider} className="flex items-center gap-3 p-3 rounded-lg border bg-white hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{p.display_name}</p>
                      <Badge variant="outline" className="text-xs">{p.provider}</Badge>
                      {p.activo && p.api_key_set && (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">activo</Badge>
                      )}
                      {!p.api_key_set && (
                        <Badge variant="secondary" className="text-xs">sin configurar</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {p.api_key_set ? `Key: ${p.api_key_preview}` : 'Sin API key'}
                      {p.model_default ? ` · Modelo: ${p.model_default}` : ''}
                      {p.base_url ? ` · ${p.base_url}` : ''}
                    </p>
                    {testResult?.provider === p.provider && (
                      <p className={`text-xs mt-1 flex items-center gap-1 ${testResult.ok ? 'text-green-700' : 'text-red-700'}`}>
                        {testResult.ok
                          ? <CheckCircle2 className="w-3.5 h-3.5" />
                          : <AlertTriangle className="w-3.5 h-3.5" />}
                        {testResult.mensaje}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {p.api_key_set && (
                      <Button size="sm" variant="outline" onClick={() => handleTest(p.provider)} disabled={testing === p.provider} data-testid={`btn-test-${p.provider}`}>
                        {testing === p.provider ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Probar'}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`btn-edit-${p.provider}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {p.api_key_set && (
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(p.provider)} data-testid={`btn-del-${p.provider}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <ProveedorIADialog
          proveedor={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); cargar(); }}
        />
      )}
    </>
  );
}

function ProveedorIADialog({ proveedor, onClose, onSaved }) {
  const [base_url, setBaseUrl] = useState(proveedor.base_url || '');
  const [api_key, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [model_default, setModelDefault] = useState(proveedor.model_default || '');
  const [activo, setActivo] = useState(!!proveedor.activo);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/hipotecas/proveedores-ia/${proveedor.provider}`,
        { base_url, api_key, model_default, activo },
        tokenHeader(),
      );
      toast({ title: 'Guardado', description: proveedor.display_name });
      onSaved();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="proveedor-ia-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cpu className="w-5 h-5 text-purple-600" />
            {proveedor.display_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">Base URL {proveedor.provider === 'custom' && <span className="text-red-600">(obligatoria)</span>}</Label>
            <Input
              value={base_url}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://api.proveedor.com/v1"
              data-testid="input-base-url"
            />
            <p className="text-xs text-gray-500 mt-1">Déjalo vacío para usar la URL por defecto del proveedor.</p>
          </div>

          <div>
            <Label className="text-xs">API Key</Label>
            <div className="flex gap-2">
              <Input
                type={showKey ? 'text' : 'password'}
                value={api_key}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={proveedor.api_key_set ? `(actual: ${proveedor.api_key_preview}) — déjalo vacío para no cambiarla` : 'sk-…'}
                data-testid="input-api-key"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => setShowKey(!showKey)}>
                <Eye className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-xs">Modelo por defecto</Label>
            <Input
              value={model_default}
              onChange={(e) => setModelDefault(e.target.value)}
              placeholder={proveedor.modelos_sugeridos?.[0] || 'modelo-id'}
              data-testid="input-model-default"
            />
            {proveedor.modelos_sugeridos?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                <span className="text-xs text-gray-500">Sugeridos:</span>
                {proveedor.modelos_sugeridos.map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setModelDefault(m)}
                    className="text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 hover:bg-purple-100"
                  >{m}</button>
                ))}
              </div>
            )}
          </div>

          <ToggleRow
            label="Activo"
            help="Cuando esté activo, este proveedor aparecerá en el selector del análisis IA."
            checked={activo}
            onCheckedChange={setActivo}
            testid="switch-activo"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="btn-save-proveedor-ia">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
